import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Box,
  Chip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Snackbar,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import { 
  Book as BookIcon, 
  Schedule as ScheduleIcon,
  AssignmentReturn as ReturnIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import type { BookLoan } from '../../types/dashboard';
import { logger } from '../../utils/logger';


interface CurrentLoansProps {
  loans: BookLoan[];
  onReturnBooks?: (loanIds: string[]) => Promise<void>;
}

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Ngày không hợp lệ';
    }
    return date.toLocaleDateString('vi-VN');
  } catch (error) {
    return 'Ngày không hợp lệ';
  }
};

const getLoanStatusColor = (loanDate: string, dueDate: string) => {
  try {
    const due = new Date(dueDate);
    const now = new Date();
    const diffInDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays < 0) return 'error';
    if (diffInDays <= 3) return 'warning';
    return 'success';
  } catch (error) {
    return 'default';
  }
};

const getDaysSinceLoan = (loanDate: string) => {
  try {
    const loan = new Date(loanDate);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - loan.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays < 0) return 'Chưa mượn';
    if (diffInDays === 0) return 'Đã mượn hôm nay';
    if (diffInDays === 1) return 'Đã mượn 01 ngày';
    return `Đã mượn ${String(diffInDays).padStart(2, '0')} ngày`;
  } catch (error) {
    return 'Ngày không hợp lệ';
  }
};

interface GroupedLoan {
  bookId: string;
  book: BookLoan['book'];
  quantity: number;
  loanIds: string[];
  loanDate: string;
  dueDate: string;
}

export const CurrentLoans: React.FC<CurrentLoansProps> = ({ loans, onReturnBooks }) => {
  const [selectedLoans, setSelectedLoans] = useState<string[]>([]);
  // State để lưu số lượng muốn trả cho mỗi cuốn sách (grouped by bookId)
  const [returnQuantities, setReturnQuantities] = useState<Record<string, number>>({});
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [isReturning, setIsReturning] = useState(false);
  const [pendingSnackbar, setPendingSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Group loans by book
  const groupedLoans = React.useMemo(() => {
    const grouped = new Map<string, GroupedLoan>();

    loans.forEach(loan => {
      const bookId = loan.bookId || loan.book?.id || '';
      if (!bookId) {
        return;
      }
      
      if (grouped.has(bookId)) {
        const existing = grouped.get(bookId)!;
        existing.quantity += 1;
        existing.loanIds.push(loan.id);
        // Use the earliest loan date
        if (loan.loanDate && (!existing.loanDate || new Date(loan.loanDate) < new Date(existing.loanDate))) {
          existing.loanDate = loan.loanDate;
        }
        // Use the earliest due date
        if (loan.dueDate && (!existing.dueDate || new Date(loan.dueDate) < new Date(existing.dueDate))) {
          existing.dueDate = loan.dueDate;
        }
      } else {
        grouped.set(bookId, {
          bookId,
          book: loan.book,
          quantity: 1,
          loanIds: [loan.id],
          loanDate: loan.loanDate || '',
          dueDate: loan.dueDate || ''
        });
      }
    });
    
    return Array.from(grouped.values());
  }, [loans]);

  const handleSelectLoan = (loanIds: string[]) => {
    setSelectedLoans(prev => {
      const allSelected = loanIds.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !loanIds.includes(id));
      } else {
        return [...new Set([...prev, ...loanIds])];
      }
    });
  };

  const handleSelectAll = () => {
    const allLoanIds = groupedLoans.flatMap(g => g.loanIds);
    if (selectedLoans.length === allLoanIds.length) {
      setSelectedLoans([]);
    } else {
      setSelectedLoans(allLoanIds);
    }
  };

  const handleReturnClick = () => {
    if (selectedLoans.length === 0) {
      setSnackbarMessage('⚠️ Vui lòng chọn ít nhất một cuốn sách để trả');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    // Khởi tạo returnQuantities cho các sách đã chọn
    const quantities: Record<string, number> = {};
    groupedLoans.forEach(grouped => {
      const selectedCount = grouped.loanIds.filter(id => selectedLoans.includes(id)).length;
      if (selectedCount > 0) {
        quantities[grouped.bookId] = selectedCount; // Mặc định trả tất cả số lượng đã chọn
      }
    });
    setReturnQuantities(quantities);
    setReturnDialogOpen(true);
  };

  const handleReturnConfirm = async () => {
    // Tính toán loanIds để trả dựa trên returnQuantities
    const loanIdsToReturn: string[] = [];
    let totalReturnQuantity = 0;
    
    groupedLoans.forEach(grouped => {
      const quantityToReturn = returnQuantities[grouped.bookId] || 0;
      if (quantityToReturn > 0 && quantityToReturn <= grouped.quantity) {
        // Lấy N loan IDs đầu tiên từ selectedLoans (N = quantityToReturn)
        const selectedLoanIds = grouped.loanIds.filter(id => selectedLoans.includes(id));
        const loanIds = selectedLoanIds.slice(0, quantityToReturn);
        loanIdsToReturn.push(...loanIds);
        totalReturnQuantity += quantityToReturn;
      }
    });
    
    if (loanIdsToReturn.length === 0) {
      setSnackbarMessage('⚠️ Vui lòng chọn số lượng sách muốn trả');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!onReturnBooks) {
      logger.log('Returning books:', loanIdsToReturn);
      setSnackbarMessage(`✅ Đã trả thành công ${totalReturnQuantity} cuốn sách`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setReturnDialogOpen(false);
      setSelectedLoans([]);
      setReturnQuantities({});
      return;
    }

    try {
      setIsReturning(true);
      await onReturnBooks(loanIdsToReturn);
      
      // Lưu thông tin snackbar để hiển thị sau khi dialog đóng
      setPendingSnackbar({
        message: `✅ Đã trả thành công ${totalReturnQuantity} cuốn sách và cập nhật kho sách`,
        severity: 'success'
      });
      
      // Đóng dialog và clear state
      setReturnDialogOpen(false);
      setSelectedLoans([]);
      setReturnQuantities({});
    } catch (error) {
      // Lưu thông tin snackbar lỗi để hiển thị sau khi dialog đóng
      setPendingSnackbar({
        message: '❌ Có lỗi xảy ra khi trả sách. Vui lòng thử lại.',
        severity: 'error'
      });
      
      // Đóng dialog
      setReturnDialogOpen(false);
    } finally {
      setIsReturning(false);
    }
  };

  const handleReturnCancel = () => {
    setReturnDialogOpen(false);
    setPendingSnackbar(null); // Hủy pending snackbar nếu có
  };

  // Hiển thị pending snackbar sau khi dialog đóng
  useEffect(() => {
    if (!returnDialogOpen && pendingSnackbar) {
      // Dialog đã đóng, hiển thị snackbar
      setTimeout(() => {
        setSnackbarMessage(pendingSnackbar.message);
        setSnackbarSeverity(pendingSnackbar.severity);
        setSnackbarOpen(true);
        setPendingSnackbar(null);
      }, 200); // Delay để dialog đóng hoàn toàn
    }
  }, [returnDialogOpen, pendingSnackbar]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const selectedLoansData = loans.filter(loan => selectedLoans.includes(loan.id));
  
  // Tính tổng số lượng sẽ trả dựa trên returnQuantities (nếu dialog đã mở) hoặc selectedCount
  const totalSelectedQuantity = Object.keys(returnQuantities).length > 0
    ? Object.values(returnQuantities).reduce((sum, qty) => sum + qty, 0)
    : groupedLoans
        .filter(g => g.loanIds.some(id => selectedLoans.includes(id)))
        .reduce((sum, g) => {
          const selectedCount = g.loanIds.filter(id => selectedLoans.includes(id)).length;
          return sum + selectedCount;
        }, 0);

  return (
    <>
      <Card>
        <CardHeader 
          title="Sách Đang Mượn" 
          action={
            groupedLoans.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedLoans.length > 0 && selectedLoans.length === groupedLoans.flatMap(g => g.loanIds).length}
                      indeterminate={selectedLoans.length > 0 && selectedLoans.length < groupedLoans.flatMap(g => g.loanIds).length}
                      onChange={handleSelectAll}
                    />
                  }
                  label="Chọn tất cả"
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={selectedLoans.length === 0}
                  onClick={handleReturnClick}
                  startIcon={<CheckCircleIcon />}
                >
                  Trả sách ({totalSelectedQuantity > 0 ? totalSelectedQuantity : selectedLoans.length})
                </Button>
              </Box>
            )
          }
        />
        <CardContent>
          {groupedLoans.length === 0 ? (
            <Alert severity="info">
              Bạn chưa mượn cuốn sách nào
            </Alert>
          ) : (
            <List>
              {groupedLoans.map((grouped) => (
                <ListItem key={grouped.bookId} divider>
                  <Checkbox
                    checked={grouped.loanIds.every(id => selectedLoans.includes(id))}
                    indeterminate={grouped.loanIds.some(id => selectedLoans.includes(id)) && !grouped.loanIds.every(id => selectedLoans.includes(id))}
                    onChange={() => handleSelectLoan(grouped.loanIds)}
                    sx={{ mr: 1 }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <BookIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="medium" component="div">
                        {grouped.book?.title || 'Sách không xác định'}
                      </Typography>
                      {grouped.quantity > 1 && (
                        <Chip 
                          label={`Số lượng: ${grouped.quantity}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Box mt={1}>
                      <Typography variant="body2" color="text.secondary" component="div">
                        Tác giả: {grouped.book?.author || 'Không xác định'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div">
                        Ngày mượn: {formatDate(grouped.loanDate)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <ScheduleIcon fontSize="small" color="action" />
                        <Chip
                          label={getDaysSinceLoan(grouped.loanDate)}
                          size="small"
                          color={getLoanStatusColor(grouped.loanDate, grouped.dueDate) as any}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </Box>
                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<ReturnIcon />}
                      onClick={() => {
                        setSelectedLoans(grouped.loanIds);
                        // Khởi tạo returnQuantities cho cuốn sách này
                        setReturnQuantities({
                          [grouped.bookId]: grouped.quantity // Mặc định trả tất cả
                        });
                        setReturnDialogOpen(true);
                      }}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Trả sách
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Return Confirmation Dialog */}
      <Dialog
        open={returnDialogOpen}
        onClose={handleReturnCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Xác nhận trả sách
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Chọn số lượng sách muốn trả cho mỗi cuốn sách:
          </Typography>
          <Box sx={{ mt: 2 }}>
            {groupedLoans
              .filter(grouped => grouped.loanIds.some(id => selectedLoans.includes(id)))
              .map((grouped) => {
                const selectedCount = grouped.loanIds.filter(id => selectedLoans.includes(id)).length;
                const currentQuantity = returnQuantities[grouped.bookId] || selectedCount;
                const maxQuantity = grouped.quantity;
                
                return (
                  <Box 
                    key={grouped.bookId} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 2,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <BookIcon color="primary" sx={{ mr: 2 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {grouped.book?.title || 'Sách không xác định'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {grouped.book?.author || 'Không xác định'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Đã mượn: {grouped.quantity} cuốn
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '200px' }}>
                      <TextField
                        type="number"
                        size="small"
                        label="Số lượng trả"
                        value={currentQuantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          const clampedValue = Math.max(0, Math.min(value, grouped.quantity));
                          setReturnQuantities(prev => ({
                            ...prev,
                            [grouped.bookId]: clampedValue
                          }));
                        }}
                        inputProps={{
                          min: 0,
                          max: grouped.quantity,
                          style: { textAlign: 'center' }
                        }}
                        sx={{ width: '100px' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        / {grouped.quantity}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Sau khi trả sách, số lượng sách có sẵn trong kho sẽ được cập nhật tự động.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReturnCancel} disabled={isReturning}>
            Hủy
          </Button>
          <Button 
            onClick={handleReturnConfirm} 
            color="primary" 
            variant="contained"
            disabled={isReturning}
            startIcon={<CheckCircleIcon />}
          >
            {isReturning ? 'Đang trả...' : 'Xác nhận trả sách'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ 
          mt: 8, // Add margin top to avoid header overlap
          zIndex: (theme) => theme.zIndex.modal + 1 // Ensure it's above dialog (modal z-index is 1300)
        }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ 
            width: '100%',
            fontSize: '14px',
            fontWeight: 'medium'
          }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};